import { useEffect, useMemo, useState } from "react";

import { SelectedOrganizationValueIdentifier } from "@/Administration/Components/Users/SelectedOrganizationValueIdentifier";
import { ClinicSettings } from "@/Common/Api/MedfarDictionaries/Dto/Responses/ClinicSettings";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { FilteredOrganizationRules } from "@/Common/Utilities/FilteredOrganizationRules";
import { Button } from "@/DesignSystem";

import styles from "./UserTypeAssignmentPopup.module.scss";

interface IUserTypeAssignmentPopupProps {
    editedUser: EmployeeUIModel;
    onCancel: () => void;
    onContinue: (editedUserModel: EmployeeUIModel) => void;
    showAccountType?: boolean;
    popupTitle?: string;
    popupMessage?: string;
    clinicSettings?: ClinicSettings;
}

function UserTypeAssignmentPopup(props: IUserTypeAssignmentPopupProps): JSX.Element {
    const noOrganizationCode = "-";
    const filteredOrganizationRules = useMemo<FilteredOrganizationRules>(() => getFilteredOrganizations(props.editedUser), []);

    const [editedUser, setEditedUser] = useState<EmployeeUIModel>(props.editedUser);
    const [selectedOrganizationValueIdentifier, setSelectedOrganizationValueIdentifier] = useState<string>(getCurrentOrganizationIdentifier());

    const { getText } = useTranslation();
    const specialtyOptions = useMemo<LicenceOrganisationSpecialisation[]>(
        () => getSpecialtyOptions(editedUser.getIssuingOrgRule()?.Id).sortByProperty(p => LicenceOrganisationSpecialisation.getSpecialtyName(p)),
        [editedUser.Model.Licences[0]?.IssuingLicenseOrganizationRuleId]
    );
    const userTypesCombo = useMemo<ComboBoxDropDownItem[]>(
        () => [
            new ComboBoxDropDownItem(EmployeeType.Assistant.toString(), getText("Enums_EmployeeType_Assistant")),
            new ComboBoxDropDownItem(EmployeeType.Practician.toString(), getText("Enums_EmployeeType_Practician"))
        ],
        []
    );

    useEffect(() => autoCompleteSpecialty(), [editedUser.Model.Licences[0]?.IssuingLicenseOrganizationRuleId]);

    const c2Width: React.CSSProperties = { width: "70%", paddingRight: "0px" };

    function getCurrentOrganizationIdentifier(): string {
        if (!editedUser?.getIssuingOrgRule()?.OrganisationCode && isValidLicenceOrganizationRule())
            return noOrganizationCode;

        return SelectedOrganizationValueIdentifier.generateIdentifier(editedUser.getIssuingOrgRule());
    }

    // Validation
    function isValidOrganizationSelection(): boolean {
        return !IsNullOrWhiteSpace(selectedOrganizationValueIdentifier);
    }

    function isTitleValid(): boolean {
        return editedUser.getIssuingOrgRule()?.isActive;
    }

    function isValidLicenceOrganizationRule(): boolean {
        const userLicenceOrganizationRule = editedUser?.getIssuingOrgRule();
        const license = editedUser?.Model?.Licences?.firstOrDefault();

        const isOrganizationInAvailableOrganizationRules = filteredOrganizationRules.isOrganizationInAvailableOrganizationRules(userLicenceOrganizationRule);

        if (license?.shouldUpdateLicenseOrganizationRuleIdOnNextLogin())
            return isOrganizationInAvailableOrganizationRules && userLicenceOrganizationRule.isActive;

        return isOrganizationInAvailableOrganizationRules;
    }

    function isSaveButtonEnabled(): boolean {
        if (editedUser.Model.Type === EmployeeType.Assistant)
            return true;

        return isValidLicenceOrganizationRule();
    }

    // On change functions
    function onOrganizationSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const orgIdentifier = e.target.value;
        const newUser = editedUser.clone();
        newUser.setIssuingOrgRule("");
        newUser.Model.LicenseOrganizationSpecializationId = "";

        setSelectedOrganizationValueIdentifier(orgIdentifier);
        setEditedUser(newUser);
    }

    function onTitleSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const licenceOrganizationRuleId = e.target.value;
        const newUser: EmployeeUIModel = editedUser.clone();
        newUser.setIssuingOrgRule(licenceOrganizationRuleId);
        newUser.Model.LicenseOrganizationSpecializationId = "";

        setEditedUser(newUser);
    }

    function onSpecialtySelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const licenseOrganizationSpecializationId = e.target.value;
        const newUser: EmployeeUIModel = editedUser.clone();
        newUser.Model.LicenseOrganizationSpecializationId = licenseOrganizationSpecializationId;
        setEditedUser(newUser);
    }

    function onTypeSelect(type: string): void {
        const newUser: EmployeeUIModel = editedUser.clone();
        const employeeType = EnumHelper.getValue(EmployeeType, type);

        newUser.Model.Type = employeeType;

        if (employeeType === EmployeeType.Assistant) {
            newUser.Model.LicenseOrganizationSpecializationId = null;
            newUser.setIssuingOrgRule("");
        }

        setEditedUser(newUser);
    }

    // Conditions to show the different selectable options
    function showOrgSelection(): boolean {
        return filteredOrganizationRules.envHasLinkedOrgRules || !!editedUser?.getIssuingOrgRule()?.OrganisationCode;
    }

    function showTitleSelection(): boolean {
        return (
            (isValidOrganizationSelection() && filteredOrganizationRules.envHasLinkedOrgRules)
            || (filteredOrganizationRules.envHasNotLinkedOrgRules && !filteredOrganizationRules.envHasLinkedOrgRules)
        );
    }

    function showSpecialtySelection(): boolean {
        return isTitleValid() && isValidLicenceOrganizationRule() && specialtyOptions?.length > 0;
    }

    /**
     * Function to autocomplete specialty when only one specialty is mapped to the selected title
     */
    function autoCompleteSpecialty(): void {
        if (editedUser.Model.LicenseOrganizationSpecializationId)
            return;

        const newUser = editedUser.clone();
        newUser.Model.LicenseOrganizationSpecializationId = specialtyOptions?.length === 1
            ? specialtyOptions[0].Id
            : "";
        setEditedUser(newUser);
    }

    // Render functions
    function renderUserPractice(): JSX.Element {
        return (
            <div className="padding-xs-top">
                <table>
                    <tbody>
                        {props.showAccountType && (
                            <>
                                <tr>
                                    <td className="c1 padding-xs-top">
                                        <label className="form-label">
                                            {getText("TypeLabel")}
                                        </label>
                                    </td>
                                    <td className="c2" style={c2Width}>
                                        <ComboBoxControl
                                            id="Type"
                                            onChange={type => onTypeSelect(type)}
                                            items={Immutable.List(userTypesCombo)}
                                            selectedCode={editedUser.Model.Type.toString()}
                                            dataCy="userProfile-accountTypeChange"
                                        />
                                    </td>
                                </tr>
                            </>
                        )}
                        {editedUser.Model.Type === EmployeeType.Practician && (
                            <>
                                {showOrgSelection() && (
                                    <tr>
                                        <td className="c1 padding-xs-top">
                                            <label className="form-label">
                                                {getText("OrganizationLabel")}
                                            </label>
                                        </td>
                                        <td className="c2" style={c2Width}>
                                            {renderOrganizationSelect()}
                                        </td>
                                    </tr>
                                )}
                                {showTitleSelection() && (
                                    <tr>
                                        <td className="c1 padding-xs-top">
                                            <label className="form-label">
                                                {getText("TitleLabel")}
                                            </label>
                                        </td>
                                        <td className="c2" style={c2Width}>
                                            {renderTitleSelect()}
                                        </td>
                                    </tr>
                                )}
                                {showSpecialtySelection() && (
                                    <tr>
                                        <td className="c1 padding-xs-top">
                                            <label className="form-label">
                                                {getText("SpecialtyLabel")}
                                            </label>
                                        </td>
                                        <td className="c2" style={c2Width}>
                                            {renderSpecialtySelect()}
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderOrganizationSelect(): JSX.Element {
        let classname = "form-control myle-control ellipsis-auto width-100";

        if (!isValidOrganizationSelection())
            classname += " invalid";

        return (
            <div>
                <select
                    id="licensedOrganisationSelect"
                    onChange={e => onOrganizationSelect(e)}
                    className={classname}
                    value={selectedOrganizationValueIdentifier ?? ""}
                    title={filteredOrganizationRules.getOrganizationAcronymAndNameById(selectedOrganizationValueIdentifier)}
                    data-cy="login-organizationSelect"
                >
                    <option />
                    <option key={noOrganizationCode} value={noOrganizationCode}>-</option>
                    {renderOrganizationsSelectableOptions()}
                </select>
            </div>
        );
    }

    function renderOrganizationsSelectableOptions(): JSX.Element[] {
        const countryCodes = filteredOrganizationRules.getCountryCodes();

        if (countryCodes.length === 1)
            return renderOrganizationsSelectableOptionsByState(countryCodes[0]);

        return countryCodes.map(countryCode => (
            <optgroup key={countryCode} label={filteredOrganizationRules.getCountryLabel(countryCode)}>
                {renderOrganizationsSelectableOptionsByState(countryCode)}
            </optgroup>
        ));
    }

    function renderOrganizationsSelectableOptionsByState(countryCode: string): JSX.Element[] {
        const stateCodes = filteredOrganizationRules.getStateCodes(countryCode);

        return stateCodes.map(stateCode => (
            <optgroup key={`${countryCode}_${stateCode}`} label={filteredOrganizationRules.getStateLabel(countryCode, stateCode)}>
                {filteredOrganizationRules
                    .getDistinctOrganizations(countryCode, stateCode)
                    .map(p => getOrganizationOption(p))
                }
            </optgroup>
        ));
    }

    function renderTitleSelect(): JSX.Element {
        let classname = "form-control myle-control";

        if (!isTitleValid())
            classname += " invalid";

        return (
            <div>
                <select
                    id="titleOrganisationSelect"
                    onChange={e => onTitleSelect(e)}
                    className={classname}
                    value={isValidLicenceOrganizationRule() ? editedUser.getIssuingOrgRule()?.Id : ""}
                    data-cy="login-titleSelect"
                >
                    <option />
                    {renderTitleSelectableOptions()}
                </select>
            </div>
        );
    }

    function renderTitleSelectableOptions(): JSX.Element[] {
        let organizationRuleTitleOptions: LicenceOrganisationRule[];

        if (selectedOrganizationValueIdentifier === noOrganizationCode || selectedOrganizationValueIdentifier === "") {
            organizationRuleTitleOptions = filteredOrganizationRules.getNoOrganizationTitles();
        }
        else {
            const org = SelectedOrganizationValueIdentifier.parseIdentifier(selectedOrganizationValueIdentifier);
            organizationRuleTitleOptions = filteredOrganizationRules.getTitlesOfOrganization(org.orgCode, org.countryCode, org.stateCode);
        }

        return organizationRuleTitleOptions.map(p => getTitleOption(p));
    }

    function renderSpecialtySelect(): JSX.Element {
        return (
            <div>
                <select
                    id="LicenseOrganizationSpecialization"
                    onChange={e => onSpecialtySelect(e)}
                    className="form-control myle-control"
                    value={editedUser.Model.LicenseOrganizationSpecializationId ?? ""}
                    data-cy="login-specialtySelect"
                >
                    <option />
                    {renderSpecialtySelectableOptions()}
                </select>
            </div>
        );
    }

    function renderSpecialtySelectableOptions(): JSX.Element[] {
        return specialtyOptions?.map(p => getSpecialtyOption(p));
    }

    function getSpecialtyOption(licenseOrganizationSpecialization: LicenceOrganisationSpecialisation): JSX.Element {
        return (
            <option
                key={licenseOrganizationSpecialization.Id}
                value={licenseOrganizationSpecialization.Id}
            >
                {LicenceOrganisationSpecialisation.getSpecialtyName(licenseOrganizationSpecialization)}
            </option>
        );
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footerContainer}>

                <Button
                    variant="neutral"
                    category="secondary"
                    dataCy="login-cancelButton"
                    onClick={() => props.onCancel()}
                    label={cacheStore.GetString("CancelLabel")}
                />
                <Button
                    variant="brand"
                    category="primary"
                    disabled={!isSaveButtonEnabled()}
                    onClick={() => props.onContinue(editedUser)}
                    dataCy="login-continueButton"
                    label={cacheStore.GetString("SaveLabel")}
                />

            </div>
        );
    }

    return (
        <Modal
            title={props.popupTitle}
            footer={renderFooter()}
            showOverlay
            cssClass="popup"
            dataCy="login-userTypeAssignmentPopup"
        >
            <label>{props.popupMessage}</label>
            <div className="psingle-column practice centered">
                <div className="tb">
                    {renderUserPractice()}
                </div>
            </div>
        </Modal>
    );
}

function getOrganizationOption(licenseOrganizationRule: LicenceOrganisationRule): JSX.Element {
    return (
        <option
            key={licenseOrganizationRule.Id}
            value={SelectedOrganizationValueIdentifier.generateIdentifier(licenseOrganizationRule)}
        >
            {LicenceOrganisationRule.getOrganizationAcronymAndName(licenseOrganizationRule)}
        </option>
    );
}

function getTitleOption(organizationRule: LicenceOrganisationRule): JSX.Element {
    return (
        <option
            key={organizationRule.Id}
            value={organizationRule.Id}
        >
            {LicenceOrganisationRule.getTitleName(organizationRule)}
        </option>
    );
}

function getFilteredOrganizations(editedUser: EmployeeUIModel): FilteredOrganizationRules {
    const filteredOrganizationRules = new FilteredOrganizationRules(cacheStore.getFilteredOrganizationRulesParam());
    const activeSite = editedUser?.getActiveSitePrimaryAddress();
    filteredOrganizationRules.createLicenceOrganizationRulesByCountryAndState(
        null,
        activeSite?.CountryLookupId,
        activeSite?.StateLookupId
    );

    return filteredOrganizationRules;
}

function getSpecialtyOptions(orgRuleId: string): LicenceOrganisationSpecialisation[] {
    return cacheStore.TitlesAndSpecialtiesCache
        .filter(p => p.Model.licenceOrganisationRuleModel.Id == orgRuleId && p.Model.licenceOrganisationSpecialisationModel.IsActive)
        .map(p => p.Model.licenceOrganisationSpecialisationModel)
        .toArray();
}

export { UserTypeAssignmentPopup };
